/* AllySabers R7.10: delivery estimates that appear after the page loads (cart drawer).
   Any [data-ig-eta] element gets today's date + its business-day range, the same way the product page does,
   including elements added later when the cart drawer re-renders. */
(() => {
  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) left -= 1;
    }
    return d;
  };

  const fill = () => {
    const els = document.querySelectorAll('[data-ig-eta]:not([data-ready])');
    if (!els.length) return;
    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
    const now = new Date();
    els.forEach(el => {
      const out = el.querySelector('[data-eta-range]');
      if (!out) return;
      const min = parseInt(el.dataset.min, 10) || 8;
      const max = parseInt(el.dataset.max, 10) || 12;
      out.textContent = `${fmt.format(addBusinessDays(now, min))} – ${fmt.format(addBusinessDays(now, max))}`;
      el.hidden = false;
      el.dataset.ready = '';
    });
  };

  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fill(); });
  };

  fill();
  new MutationObserver(queue).observe(document.body, { childList: true, subtree: true });
})();

/* R7.11: homepage review wall, evened out. On wider screens the first row keeps the page order (photo reviews
   first) and the rest are dealt into the columns in whichever way makes them end most evenly, so short text-only
   reviews don't all pile into the last column and leave a hole. If the columns then end
   within ~90px of each other, the last card in each short column grows a little so the bottom edge lines up.
   Phones keep the swipe rail. Without this script the wall falls back to CSS columns. */
(() => {
  const UNIT = 2; // grid row height in px
  const GAP = 16; // space under each card
  const STRETCH = 90; // most a card may grow, in px

  // Greedy: each card (in the given order) into the shortest column.
  const greedy = (spans, cols, order) => {
    const heights = new Array(cols).fill(0);
    const colOf = [];
    order.forEach(i => {
      let col = 0;
      for (let c = 1; c < cols; c++) if (heights[c] < heights[col]) col = c;
      colOf[i] = col;
      heights[col] += spans[i];
    });
    return colOf;
  };

  // Exhaustive: the first row keeps the page order; every way of dealing the remaining cards (up to 9 of them)
  // into the columns is tried and the one whose columns end closest together wins.
  const evenest = (spans, cols) => {
    const first = Math.min(cols, spans.length);
    const rest = spans.length - first;
    if (rest > 9) return null;
    const h = spans.slice(0, first).concat(new Array(cols - first).fill(0));
    const pick = new Array(rest);
    let best = null;
    let bestSpread = Infinity;
    const step = k => {
      if (k === rest) {
        const spread = Math.max(...h) - Math.min(...h);
        if (spread < bestSpread) { bestSpread = spread; best = pick.slice(); }
        return;
      }
      for (let c = 0; c < cols; c++) {
        pick[k] = c;
        h[c] += spans[first + k];
        step(k + 1);
        h[c] -= spans[first + k];
      }
    };
    step(0);
    return spans.map((s, i) => (i < first ? i : best[i - first]));
  };

  const balance = wall => {
    const cards = [...wall.children].filter(c => c.classList.contains('ig-review'));
    const cols = window.innerWidth >= 1200 ? 4 : window.innerWidth >= 800 ? 3 : 0;
    cards.forEach(c => {
      c.style.gridColumn = '';
      c.style.gridRow = '';
      c.classList.remove('is-stretched');
    });
    if (!cols || cards.length <= cols) {
      wall.classList.remove('is-balanced');
      return;
    }
    wall.style.setProperty('--wall-cols', cols);
    wall.classList.add('is-balanced');

    const spans = cards.map(c => Math.ceil((c.getBoundingClientRect().height + GAP) / UNIT));
    const colOf = evenest(spans, cols) || greedy(spans, cols, cards.map((c, i) => i));
    const heights = new Array(cols).fill(0);
    const starts = [];
    const last = new Array(cols).fill(-1);
    cards.forEach((card, i) => {
      const col = colOf[i];
      starts[i] = heights[col] + 1;
      heights[col] += spans[i];
      last[col] = i;
      card.style.gridColumn = String(col + 1);
      card.style.gridRow = `${starts[i]} / span ${spans[i]}`;
    });

    const max = Math.max(...heights);
    last.forEach((i, col) => {
      const short = max - heights[col];
      if (i < 0 || short <= 0 || short * UNIT > STRETCH) return;
      cards[i].style.gridRow = `${starts[i]} / span ${spans[i] + short}`;
      cards[i].classList.add('is-stretched');
    });
  };

  const run = () => document.querySelectorAll('.ig-reviews__wall').forEach(balance);

  run();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
  window.addEventListener('load', run);
  let width = window.innerWidth;
  let timer;
  window.addEventListener('resize', () => {
    if (window.innerWidth === width) return;
    width = window.innerWidth;
    clearTimeout(timer);
    timer = setTimeout(run, 150);
  });
  document.addEventListener('shopify:section:load', run);
})();
